<template>
    <div>
        <b-modal
            id="modal-customer-user"
            :title="`${customer && customer.name}-${
                userModel.id ? 'Edit Contact' : 'New Contact'
            }`"
            @ok="handleOk"
        >
            <template #default>
                <b-form ref="contactForm" @submit.stop.prevent="handleSubmit">
                    <b-form-group
                        id="input-group-email"
                        :label="`Email:${userModel.is_blacklisted ? '' : '*'}`"
                        label-for="input-email"
                        label-cols-md="3"
                    >
                        <b-form-input
                            id="input-email"
                            v-model="displayEmail"
                            type="email"
                            placeholder="Enter email"
                            :required="
                                isEmailViewOnly || !userModel.is_blacklisted
                            "
                            :disabled="!isEmailViewOnly && !!userModel.id"
                        ></b-form-input>
                    </b-form-group>

                    <b-form-group
                        id="input-group-is_blacklisted"
                        label="Blocked:"
                        label-for="input-is_blacklisted"
                        label-cols-md="3"
                        v-if="!isEmailViewOnly"
                    >
                        <b-form-checkbox
                            id="input-is_blacklisted"
                            v-model="is_blacklisted"
                            name="input-is_blacklisted"
                            :disabled="isDummyEmail"
                        ></b-form-checkbox>
                    </b-form-group>

                    <b-form-group
                        id="input-group-phone"
                        label="Phone:"
                        label-for="input-phone"
                        label-cols-md="3"
                        v-if="!isEmailViewOnly"
                    >
                        <b-form-input
                            id="input-phone"
                            v-model="userModel.phone"
                            type="number"
                            placeholder="Enter phone"
                        ></b-form-input>
                    </b-form-group>

                    <b-form-group
                        id="input-group-2"
                        label="First Name*"
                        label-for="input-2"
                        label-cols-md="3"
                        v-if="!isEmailViewOnly"
                    >
                        <b-form-input
                            id="input-2"
                            v-model="userModel.first_name"
                            placeholder="Enter First Name"
                            required
                        ></b-form-input>
                    </b-form-group>

                    <b-form-group
                        id="input-group-last-name"
                        label="Last Name*:"
                        label-for="input-last-name"
                        label-cols-md="3"
                        v-if="!isEmailViewOnly"
                    >
                        <b-form-input
                            id="input-last-name"
                            v-model="userModel.last_name"
                            placeholder="Enter Last Name"
                            required
                        ></b-form-input>
                    </b-form-group>

                    <b-form-group
                        id="input-group-remarks"
                        label="Remarks:"
                        label-for="input-remarks"
                        label-cols-md="3"
                        v-if="!isEmailViewOnly"
                    >
                        <b-form-textarea
                            id="input-remarks"
                            v-model="userModel.remarks"
                            placeholder="Enter Remarks"
                            rows="3"
                            max-rows="6"
                        ></b-form-textarea>
                    </b-form-group>
                </b-form>
            </template>
            <template #modal-footer="{ ok, cancel }">
                <b-button-group size="sm">
                    <b-button
                        v-if="!!userModel.id && hasPermissionUpdate"
                        variant="info"
                        @click="isEmailViewOnly = !isEmailViewOnly"
                    >
                        {{ isEmailViewOnly ? 'Cancel' : 'Change Email' }}
                    </b-button>
                    <b-button
                        :disabled="loading"
                        variant="success"
                        v-if="
                            (!!userModel.id && hasPermissionUpdate) ||
                            (!userModel.id && hasPermissionCreate)
                        "
                        @click="ok()"
                    >
                        <b-spinner
                            v-show="loading"
                            variant="primary"
                            label="Spinning"
                        ></b-spinner>
                        <span v-show="!loading">{{
                            `${userModel.id ? 'Update' : 'Create'}`
                        }}</span>
                    </b-button>
                    <b-button
                        v-if="
                            !isEmailViewOnly &&
                            allowDeletion &&
                            !!userModel.id &&
                            hasPermissionDelete
                        "
                        variant="danger"
                        @click="deleteUser(userModel.id)"
                    >
                        Delete
                    </b-button>
                    <b-button
                        variant="warning"
                        @click="
                            isEmailViewOnly = false
                            cancel()
                        "
                        v-if="!isEmailViewOnly"
                    >
                        Exit
                    </b-button>
                </b-button-group>
            </template>
        </b-modal>

        <b-button
            size="sm"
            class="mr-2"
            @click="viewCustomer()"
            v-if="hasPermissionCreate"
            >Add Contact</b-button
        >

        <b-table striped hover :items="contacts" :fields="fields">
            <template #cell(id)="{ item }">
                <b-button
                    size="sm"
                    class="mr-2"
                    @click="viewCustomer(item)"
                    variant="primary"
                    v-if="hasPermissionDelete"
                >
                    View
                </b-button>
            </template>
        </b-table>
    </div>
</template>

<script>
import popupMixins from '@/mixins/popupMixins'
import permissionsMixins from '@/mixins/permissionsMixins'
//const dummyEmailSuffix = 'dummy@oms.innovate10x.com'
const userModel = Object.freeze({
    first_name: null,
    last_name: null,
    email: null,
    phone: null,
    id: null,
    remarks: null,
})
export default {
    name: 'customers-users',
    mixins: [popupMixins, permissionsMixins],
    data() {
        return {
            userModel: {},
            loading: false,
            settings: {},
            isEmailViewOnly: false,
        }
    },

    props: {
        allowDeletion: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        fields() {
            const { dummyEmailSuffix } = this
            return [
                { label: '', key: 'id' },
                'first_name',
                'last_name',
                {
                    key: 'is_blacklisted',
                    label: 'Blocked',
                    formatter: value => (value==1)?"Yes":"No",
                },
                {
                    key: 'email',
                    label: 'Email',
                    formatter: value =>
                        (value || '').indexOf(dummyEmailSuffix) >= 0
                            ? ''
                            : value,
                },
                'phone',
            ]
        },
        dummyEmailSuffix() {
            console.log('emailPartsValue:', this.emailPartsValue)
            const { emailDomain, usernameSuffix } = {
                ...(this.emailPartsValue || {}),
            }
            return `-${usernameSuffix}@${emailDomain}`
        },

        emailPartsValue() {
            return this.$store.getters['company/emailPartsValue']
        },

        emailPartsKey() {
            return this.$store.state.company.settingKeys.emailPartsKey
        },

        contacts() {
            return this.$store.getters['customers/customerUsers']
        },
        customer() {
            return this.$store.state.customers.item
        },
        isDummyEmail() {
            return (
                (this.userModel.email || '').indexOf(this.dummyEmailSuffix) >= 0
            )
        },
        displayEmail: {
            set(value) {
                this.$set(this.userModel, 'email', value)
            },
            get() {
                const {
                    isDummyEmail,
                    userModel: { email },
                    isEmailViewOnly,
                } = this
                return isDummyEmail && isEmailViewOnly
                    ? ''
                    : isDummyEmail
                    ? '------'
                    : email
            },
        },
        is_blacklisted: {
            set(value) {
                this.$set(this.userModel, 'is_blacklisted', value ? 1 : 0)
            },
            get() {
                return this.isDummyEmail
                    ? true
                    : !!this.userModel.is_blacklisted
            },
        },
    },

    mounted() {
        this.$store.dispatch('company/fetchConfiguration', this.emailPartsKey)
        this.$root.$on('bv::modal::show', () => {
            this.isEmailViewOnly = false
        })
    },

    methods: {
        viewCustomer(data) {
            this.userModel = { ...userModel, ...(data || {}) }
            this.$bvModal.show('modal-customer-user')
        },

        checkFormValidity() {
            const valid = this.$refs.contactForm.checkValidity()
            return valid
        },

        handleOk(bvModalEvent) {
            // Prevent modal from closing
            bvModalEvent.preventDefault()
            // Trigger submit handler
            ///this.handleSubmit()
            this.$refs.contactForm.requestSubmit()
        },

        async handleSubmit() {
            try {
                if (!this.checkFormValidity()) {
                    throw new Error('Not Validated!')
                }
                const {
                    customer: { id: customer_id },
                    userModel: data,
                    isDummyEmail,
                    isEmailViewOnly,
                } = this
                const isNew = !data.id

                if (isEmailViewOnly) {
                    const { id: userId, email } = data
                    await this.$store.dispatch('users/updateEmail', {
                        userId,
                        email,
                    })
                    await this.$store.dispatch(
                        'customers/fetchById',
                        customer_id
                    )
                } else {
                    if (isDummyEmail) {
                        data.is_blacklisted = 1
                    }

                    console.log('data', data)
                    if (data.is_blacklisted && !data.email) {
                        data.email = this.calculateDummyEmailAddress()
                    }
                    const path = `customers/${
                        isNew ? 'createUser' : 'updateUser'
                    }`
                    this.loading = true
                    await this.$store.dispatch(path, {
                        customer_id,
                        data,
                    })
                }

                if (isNew) {
                    this.doToast('Contact Created', 'Created', 'success')
                } else {
                    this.doToast('Contact Updated', 'Updated', 'success')
                }

                this.$nextTick(() => {
                    this.$bvModal.hide('modal-customer-user')
                })
            } catch (error) {
                const emailError = 'The given data was invalid.'
                if (error.response) {
                    // Request made and server responded
                    console.log('error.response')
                    console.log(error.response.data)
                    console.log(error.response.status)
                    console.log(error.response.headers)
                    this.doToast(
                        'Error Detected',
                        error.response.data.message,
                        'warning'
                    )
                } else if (error.request) {
                    // The request was made but no response was received
                    console.log('error.request')
                    console.log(error.request)
                    this.doToast(
                        'System Error Detected',
                        'Contact Administrator',
                        'warning'
                    )
                } else {
                    // Something happened in setting up the request that triggered an Error
                    let errorMessage = error.message

                    if (errorMessage == emailError) {
                        errorMessage = 'Email is already used!!!'
                    }
                    console.log('Error', error.message)
                    this.doToast('Error Detected', errorMessage, 'warning')
                }
            } finally {
                this.loading = false
            }
        },

        calculateDummyEmailAddress() {
            const username =
                Math.random().toString(36).substring(2, 15) +
                Math.random().toString(36).substring(2, 15)
            return `${username}-${this.dummyEmailSuffix}`
        },

        async deleteUser(user_id) {
            try {
                const title = 'Delete Customer?'
                const message = `Are you sure you want to delete the Contact?`
                const result = await this.confirmMessage(title, message)
                if (!result) throw new Error('Cancel Delete!')

                const {
                    customer: { id: customer_id },
                } = this
                this.loading = true
                await this.$store.dispatch('customers/deleteUser', {
                    customer_id,
                    user_id,
                })
                this.doToast('Contact Deleted', 'Deleted', 'success')
                this.$bvModal.hide('modal-customer-user')
            } catch (err) {
                console.log('err', err)
                this.doToast('Error Detected', err.message, 'warning')
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
